// Phase 34 · 20b chrome, applied to the closed-ticket archive.
//
// This is a RESTYLE ONLY. Every behaviour of the previous page is byte-for-byte
// the same: the fetch, the search, the six filters and their panel, the
// click-outside/scroll-to-close rules, the four sortable columns, client-side
// paging with its page-size selector, the export, the Reopen modal and its
// redirect, the truncation notice, the error banner and the empty states.
// Only the markup around them changed.
//
// What 20b contributes: the floating shell (cards on the canvas, not bands
// butted against it), the header card with a mono subtitle, the quick-filter
// tab strip, the mono column headings, and the 332px rail.
//
// The rail carries facts derived from the rows already on screen — it does not
// fetch anything and it does not invent anything. Where 20b shows failures,
// events-by-hour and who-changed-things, this shows the truncation notice
// (absent when nothing is truncated, the same absent-at-zero rule), archived
// per day, and who owned them.

import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArchiveRestore,
  RefreshCw,
  Mail,
  MessageSquare,
  Phone,
  Globe,
  RotateCcw,
  Search,
  ChevronDown,
  ChevronUp,
  ArrowUpDown,
  AlertCircle,
  X,
  SlidersHorizontal,
} from 'lucide-react';
import { useToast } from '@centraoneplus/ui';
import { ticketService } from '@services/index';
import type { Ticket } from '@centraoneplus/shared-types';
import { ReopenTicketModal, type ReopenPayload } from '@components/tickets/ReopenTicketModal';
import {
  filterAndSortBacktracked,
  deriveStageGroups,
  derivePipelineOptions,
  deriveAssigneeOptions,
  UNASSIGNED_ASSIGNEE,
} from './backtrackFilter';
import { ExportButton } from '@/components/common';
import { ExportColumn, formatters } from '@utils/export';
import { PageHeaderMeta } from '@components/layouts/PageHeaderMeta';
import { ThemedSelect } from '@components/common/ThemedSelect';
import { Pager } from '@shared/ui/kit';

const CHANNEL_ICONS: Record<string, React.ElementType> = {
  email: Mail,
  chat: MessageSquare,
  phone: Phone,
  web: Globe,
};

const PRIORITY_OPTIONS = ['low', 'medium', 'high', 'urgent'];

/** Priority is a status, so it is coloured by the status ramp, never decorated. */
const PRIORITY_TONE: Record<string, { fg: string; bg: string; line: string }> = {
  urgent: {
    fg: 'var(--co-burgundy)',
    bg: 'var(--co-burgundy-wash)',
    line: 'var(--co-burgundy-line)',
  },
  high: {
    fg: 'var(--co-terracotta-ink)',
    bg: 'var(--co-terracotta-wash)',
    line: 'var(--co-terracotta-line)',
  },
  medium: {
    fg: 'var(--co-brass-ink)',
    bg: 'var(--co-brass-wash)',
    line: 'var(--co-brass)',
  },
  low: {
    fg: 'var(--co-ink-3)',
    bg: 'transparent',
    line: 'var(--co-line-strong)',
  },
};
const toneFor = (p: string) => PRIORITY_TONE[p] || PRIORITY_TONE.medium;

// How many rows we pull from the archive to filter over. The backtrack query
// caps `limit` at 200, so this is the widest single page we can filter/sort
// client-side without touching the backend.
const FETCH_LIMIT = 200;

/** The floating shell: cards sit ON the canvas, they do not butt against it. */
const CARD: React.CSSProperties = { borderRadius: 10, boxShadow: 'var(--co-shadow-card)' };
const HEADER_CARD: React.CSSProperties = { borderRadius: 14, boxShadow: 'var(--co-shadow-card)' };
const CARD_CLASS = 'border border-[var(--co-line)] bg-[var(--co-raised)]';

const CONTROL =
  'flex h-[34px] flex-none items-center gap-[7px] whitespace-nowrap rounded-[10px] border ' +
  'border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 text-[12.5px] ' +
  'text-[var(--co-ink-2)] hover:border-[var(--co-brass)] hover:bg-[var(--co-brass-wash)] ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

function formatDate(date: string | Date) {
  const d = new Date(date);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

// Ticket creation date — shown as an absolute calendar date (e.g. "Jul 2, 2026")
// since "when was this created" is more useful as a date than a relative age.
function formatCreatedDate(date: string | Date) {
  return new Date(date).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function SortableHeader({
  field,
  label,
  sortBy,
  sortOrder,
  onSort,
  className = '',
}: {
  field: string;
  label: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSort: (field: string) => void;
  className?: string;
}) {
  const isActive = sortBy === field;

  return (
    <th
      scope="col"
      aria-sort={isActive ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
      // Same type size as PlainHeader, so both kinds of heading share a line box.
      className={`px-4 py-[11px] text-left text-[9.5px] ${className}`}
    >
      <button
        type="button"
        onClick={() => onSort(field)}
        className="co-mono inline-flex items-center gap-1 text-[9.5px] font-semibold uppercase tracking-[.2em]"
        style={{ color: isActive ? 'var(--co-ink)' : 'var(--co-ink-3)' }}
      >
        {label}
        {isActive ? (
          sortOrder === 'asc' ? (
            <ChevronUp className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )
        ) : (
          <ArrowUpDown className="h-[11px] w-[11px] opacity-40" />
        )}
      </button>
    </th>
  );
}

function PlainHeader({ label, className = '' }: { label: string; className?: string }) {
  return (
    <th
      scope="col"
      className={`co-mono px-4 py-[11px] text-left text-[9.5px] font-semibold uppercase tracking-[.2em] text-[var(--co-ink-3)] ${className}`}
    >
      {label}
    </th>
  );
}

interface BacktrackRowProps {
  ticket: Ticket;
  onNavigate: () => void;
  onReopen: () => void;
  isReopening: boolean;
}

function BacktrackRow({ ticket, onNavigate, onReopen, isReopening }: BacktrackRowProps) {
  const ChannelIcon = CHANNEL_ICONS[ticket.channel] || Mail;
  const tone = toneFor(ticket.priority);
  // The left rule is reserved for the rows that cost something: an urgent
  // ticket that got archived, and the high ones behind it.
  const rule =
    ticket.priority === 'urgent'
      ? 'var(--co-burgundy)'
      : ticket.priority === 'high'
        ? 'var(--co-terracotta)'
        : 'transparent';

  return (
    <tr
      onClick={onNavigate}
      className="cursor-pointer border-b border-[var(--co-line-subtle)] hover:bg-[var(--co-selected)]"
    >
      {/* Inset shadow, not border-left: with collapsed table borders a border is
          painted at table level and bleeds over the sticky header on scroll. */}
      <td className="max-w-0 px-4 py-3" style={{ boxShadow: `inset 2px 0 0 ${rule}` }}>
        <div className="flex items-center gap-3">
          <span
            className="flex h-[26px] w-[26px] flex-none items-center justify-center rounded-[7px] border"
            style={{
              borderColor: 'var(--co-line-strong)',
              background: 'var(--co-raised)',
              color: 'var(--co-ink-3)',
            }}
          >
            <ChannelIcon className="h-[13px] w-[13px]" />
          </span>
          <div className="min-w-0 flex-1">
            {/* Both lines are `truncate`d to keep the column narrow, so the full
                value is only available on hover — without the title attribute a
                ticket number reads as "#TKT-00..." and cannot be read at all. */}
            <p
              className="truncate text-[13px] font-semibold text-[var(--co-ink)]"
              title={ticket.subject}
            >
              {ticket.subject}
            </p>
            <p
              className="co-mono mt-1 truncate text-[10.5px] uppercase text-[var(--co-ink-3)]"
              title={`#${ticket.ticketNumber || ticket.id.slice(0, 8)}`}
            >
              #{ticket.ticketNumber || ticket.id.slice(0, 8)}
            </p>
          </div>
        </div>
      </td>
      <td className="whitespace-nowrap px-4 py-3">
        <span
          className="co-mono inline-flex items-center rounded-md border px-2 py-[3px] text-[9.5px] font-semibold uppercase tracking-[.1em]"
          style={{ color: tone.fg, background: tone.bg, borderColor: tone.line }}
        >
          {ticket.priority}
        </span>
      </td>
      <td className="whitespace-nowrap px-4 py-3">
        {ticket.assigneeId ? (
          <div className="flex min-w-0 items-center gap-2">
            <span className="flex h-6 w-6 flex-none items-center justify-center rounded-[7px] bg-[var(--co-ink-3)] text-[10px] font-semibold text-[var(--co-ink-invert)]">
              {(ticket.assignee?.name || 'A').charAt(0).toUpperCase()}
            </span>
            <span className="truncate text-[12.5px] text-[var(--co-ink-2)]">
              {ticket.assignee?.name || 'Assigned'}
            </span>
          </div>
        ) : (
          <span className="text-[12.5px] text-[var(--co-ink-3)]">Unassigned</span>
        )}
      </td>
      <td className="whitespace-nowrap px-4 py-3">
        <span className="block truncate text-[12.5px] text-[var(--co-ink-2)]">
          {ticket.pipeline?.name || '—'}
        </span>
      </td>
      <td className="co-mono whitespace-nowrap px-4 py-3 text-[11.5px] text-[var(--co-ink-3)]">
        {ticket.createdAt ? formatCreatedDate(ticket.createdAt) : '—'}
      </td>
      <td className="co-mono whitespace-nowrap px-4 py-3 text-[11.5px] text-[var(--co-ink-3)]">
        {(ticket as any).backtrackedAt ? formatDate((ticket as any).backtrackedAt) : '—'}
      </td>
      <td className="whitespace-nowrap px-4 py-3">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onReopen();
          }}
          disabled={isReopening}
          className="inline-flex items-center gap-1.5 rounded-md border px-[9px] py-[5px] text-[11.5px] font-semibold disabled:cursor-not-allowed disabled:opacity-50"
          style={{
            borderColor: 'var(--co-brass)',
            background: 'var(--co-brass-wash)',
            color: 'var(--co-brass-ink)',
          }}
        >
          <RotateCcw className={`h-[13px] w-[13px] ${isReopening ? 'animate-spin' : ''}`} />
          {isReopening ? 'Reopening…' : 'Reopen'}
        </button>
      </td>
    </tr>
  );
}

export default function BacktrackPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const [rows, setRows] = useState<Ticket[]>([]);
  const [serverTotal, setServerTotal] = useState(0);
  /**
   * One count per priority across the WHOLE archive, from the service's own
   * tally. The tab strip reads it so its numbers describe the archive rather
   * than the {FETCH_LIMIT}-row page this screen holds. Null when the service
   * did not send it — the tabs then count the page and say so.
   */
  const [archiveByPriority, setArchiveByPriority] = useState<Record<string, number> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState('');

  // Filter panel (revealed by the Filter button) — rendered as a floating
  // overlay so it never pushes the table down / creates a squished inner scroll.
  const [showFilters, setShowFilters] = useState(false);
  const filterPanelRef = useRef<HTMLDivElement>(null);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const [selectedPriorities, setSelectedPriorities] = useState<string[]>([]);
  const [selectedStages, setSelectedStages] = useState<string[]>([]);
  const [selectedPipeline, setSelectedPipeline] = useState('');
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Sorting + pagination (client-side)
  const [sortBy, setSortBy] = useState('backtrackedAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Reopen
  const [reopenTarget, setReopenTarget] = useState<Ticket | null>(null);
  const [reopeningId, setReopeningId] = useState<string | null>(null);

  // Distinct pipeline stages present in the fetched archive, name-deduped into
  // groups (one chip per stage name, even if several pipelines each have their
  // own "Resolved"). Derived from `rows` (not the filtered subset) so the option
  // set stays stable as other filters are applied.
  const availableStages = useMemo(() => deriveStageGroups(rows), [rows]);
  const availablePipelines = useMemo(() => derivePipelineOptions(rows), [rows]);
  const { options: assigneeOptions, hasUnassigned } = useMemo(
    () => deriveAssigneeOptions(rows),
    [rows],
  );

  // Today (local, YYYY-MM-DD) — used to block future dates in the pickers, since
  // a ticket can't have been created or backtracked in the future.
  const todayStr = useMemo(() => {
    const d = new Date();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
  }, []);

  const fetchRows = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await ticketService.getBacktrackedTickets({ page: 1, limit: FETCH_LIMIT });
      setRows(response.data);
      setServerTotal(response.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch backtracked tickets');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Read-only, and never fatal: a failed tally leaves the tabs counting the
   * page, which is what they did before the archive could be counted at all.
   */
  const fetchArchiveCounts = async () => {
    try {
      const { byPriority } = await ticketService.getBacktrackCount();
      setArchiveByPriority(byPriority ?? null);
    } catch {
      setArchiveByPriority(null);
    }
  };

  useEffect(() => {
    fetchRows();
    void fetchArchiveCounts();
  }, []);

  // Close the filter overlay when clicking outside it (but not when clicking the
  // toggle button, which manages its own open/close). Native <select>/<input
  // type="date"> popups are browser chrome and don't fire this, so picking an
  // option/date won't dismiss the panel.
  useEffect(() => {
    if (!showFilters) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (filterPanelRef.current?.contains(target)) return;
      if (filterButtonRef.current?.contains(target)) return;
      setShowFilters(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [showFilters]);

  // Close the overlay when the user scrolls the page/table (any live filters are
  // already applied). Scrolling *within* the panel's own body is ignored.
  // Capture phase so scroll on the inner table container is caught too.
  useEffect(() => {
    if (!showFilters) return;
    const onScroll = (e: Event) => {
      if (filterPanelRef.current?.contains(e.target as Node)) return;
      setShowFilters(false);
    };
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [showFilters]);

  const handleSort = (field: string) => {
    setSortOrder((prev) => (sortBy === field && prev === 'desc' ? 'asc' : 'desc'));
    setSortBy(field);
  };

  const togglePriority = (value: string) => {
    setPage(1);
    setSelectedPriorities((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  // Toggle a whole stage group (all IDs sharing a name) on/off together.
  const toggleStageGroup = (ids: string[]) => {
    setPage(1);
    setSelectedStages((prev) => {
      const allSelected = ids.every((id) => prev.includes(id));
      if (allSelected) return prev.filter((id) => !ids.includes(id));
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return [...next];
    });
  };

  const clearFilters = () => {
    setSelectedPriorities([]);
    setSelectedStages([]);
    setSelectedPipeline('');
    setSelectedAssignee('');
    setFromDate('');
    setToDate('');
    setPage(1);
  };

  // A stage group counts once when fully selected (so the badge reflects chips
  // clicked, not the underlying per-pipeline IDs).
  const selectedStageGroupCount = availableStages.filter((g) =>
    g.ids.every((id) => selectedStages.includes(id)),
  ).length;

  const activeFilterCount =
    selectedPriorities.length +
    selectedStageGroupCount +
    (selectedPipeline ? 1 : 0) +
    (selectedAssignee ? 1 : 0) +
    (fromDate ? 1 : 0) +
    (toDate ? 1 : 0);

  // Search (subject / ticket number) then priority + date filter + sort. All
  // client-side over the fetched page. Search is applied first so the filter
  // helper only sorts/filters the already-searched subset.
  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const searched = q
      ? rows.filter(
          (t) =>
            (t.subject || '').toLowerCase().includes(q) ||
            (t.ticketNumber || '').toLowerCase().includes(q),
        )
      : rows;
    return filterAndSortBacktracked(searched, {
      priorities: selectedPriorities,
      stages: selectedStages,
      pipelineId: selectedPipeline,
      assigneeId: selectedAssignee,
      fromDate,
      toDate,
      sortBy,
      sortOrder,
    });
  }, [rows, searchQuery, selectedPriorities, selectedStages, selectedPipeline, selectedAssignee, fromDate, toDate, sortBy, sortOrder]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageRows = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const truncated = serverTotal > rows.length;

  // ---- rail figures, derived from what is already on screen ----

  /**
   * One count per priority for the tab strip. The service's archive-wide tally
   * when there is one, the fetched page when there is not — and `countScope`
   * says which, so a tab never claims the archive while counting a page.
   */
  const priorityCounts = useMemo(() => {
    if (archiveByPriority) return archiveByPriority;
    const counts: Record<string, number> = {};
    for (const t of rows) counts[t.priority] = (counts[t.priority] || 0) + 1;
    return counts;
  }, [archiveByPriority, rows]);
  const countsAreArchiveWide = Boolean(archiveByPriority);
  const countScope = countsAreArchiveWide
    ? 'the whole archive'
    : `the ${FETCH_LIMIT} most recently archived tickets`;

  /** Eight days ending today; a day with nothing archived is an empty slot. */
  const byDay = useMemo(() => {
    const days: { key: string; label: string; count: number }[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = 7; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      days.push({ key: d.toDateString(), label: String(d.getDate()), count: 0 });
    }
    for (const t of filtered) {
      const at = (t as any).backtrackedAt;
      if (!at) continue;
      const d = new Date(at);
      if (Number.isNaN(d.getTime())) continue;
      d.setHours(0, 0, 0, 0);
      const slot = days.find((x) => x.key === d.toDateString());
      if (slot) slot.count++;
    }
    return days;
  }, [filtered]);
  const dayPeak = Math.max(1, ...byDay.map((d) => d.count));

  /** Who owned them when they were archived. Unassigned is a real answer. */
  const ownerTally = useMemo(() => {
    const map = new Map<string, { key: string; name: string; count: number; none: boolean }>();
    for (const t of filtered) {
      const none = !t.assigneeId;
      const name = none ? 'Unassigned' : t.assignee?.name || 'Assigned';
      const key = none ? '__none' : t.assigneeId!;
      const prev = map.get(key);
      map.set(key, { key, name, none, count: (prev?.count ?? 0) + 1 });
    }
    return [...map.values()].sort((a, b) => b.count - a.count);
  }, [filtered]);

  const handleReopenSubmit = async (payload: ReopenPayload) => {
    if (!reopenTarget) return;
    const ticketId = reopenTarget.id;
    const ticketNumber = reopenTarget.ticketNumber;
    setReopeningId(reopenTarget.id);
    try {
      await ticketService.reopenTicket(reopenTarget.id, payload);
      setRows((prev) => prev.filter((t) => t.id !== reopenTarget.id));
      setServerTotal((prev) => Math.max(0, prev - 1));
      setReopenTarget(null);
      toast.success(
        'Ticket reopened',
        ticketNumber ? `#${ticketNumber} reopened — opening it now` : 'Ticket reopened — opening it now',
      );
      // Redirect straight to the reopened ticket so the agent lands on it.
      navigate(`/tickets/${ticketId}`);
    } catch (err) {
      toast.error(
        'Reopen failed',
        err instanceof Error ? err.message : 'Could not reopen this ticket. Please try again.',
      );
    } finally {
      setReopeningId(null);
    }
  };

  const exportColumns = useMemo<ExportColumn<Ticket>[]>(() => [
    { key: 'ticketNumber', header: 'Ticket #' },
    { key: 'subject', header: 'Subject' },
    { key: 'priority', header: 'Priority' },
    { key: 'channel', header: 'Channel' },
    {
      key: 'assigneeId',
      header: 'Assigned To',
      formatter: (_value, row) => {
        if (row.assignee?.name) return row.assignee.name;
        if (row.assignee?.email) return row.assignee.email;
        return row.assigneeId ? '—' : 'Unassigned';
      },
    },
    { key: 'pipelineId', header: 'Pipeline', formatter: (_value, row) => row.pipeline?.name || '' },
    {
      key: 'createdAt',
      header: 'Created',
      formatter: (value) => formatters.dateTime(value),
    },
    {
      key: 'backtrackedAt',
      header: 'Backtracked',
      formatter: (_value, row) => {
        const backtrackedAt = (row as any).backtrackedAt;
        return backtrackedAt ? formatters.dateTime(backtrackedAt) : '';
      },
    },
  ], []);

  // Export the currently filtered backtracked rows — NOT the tickets list
  // store's fetchAllForExport, which hits the main tickets endpoint and has no
  // backtrackedAt field. This keeps the export in sync with what's on screen.
  const fetchBacktrackedForExport = useCallback(async () => {
    return filtered;
  }, [filtered]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-[14px] overflow-hidden bg-[var(--co-canvas)] py-4 pr-4">
      {/* ---------- header card + its bottom-right action tab ---------- */}
      <div className="relative flex flex-none flex-col">
      <header
        className={`${CARD_CLASS} relative px-5 pb-4 pt-[18px] min-[1680px]:!rounded-br-none`}
        style={HEADER_CARD}
      >
        {/* Row 1: title block + the utility cluster, nothing else. */}
        <div className="flex items-center justify-between gap-[10px]">
          <div className="min-w-0">
            <h1 className="text-[28px] font-bold leading-[1.15] tracking-[-.018em] text-[var(--co-ink)]">
              Backtrack
            </h1>
            <p className="co-mono mt-[6px] text-[10.5px] font-medium uppercase tracking-[.13em] text-[var(--co-ink-3)]">
              Closed tickets are archived here at the end of each day · {serverTotal} archived
            </p>
          </div>
          <div className="flex h-[34px] flex-none items-center">
            <PageHeaderMeta />
          </div>
        </div>

        {/* Row 2: the quick priority tabs on the left, Filter / Refresh / Export on the right. */}
        <div className="mt-3 flex flex-wrap items-center gap-4 border-t border-[var(--co-line-subtle)] pt-3">
          {/* Quick priority filter. It writes the SAME `selectedPriorities` the
              panel's chips do, so the two can never disagree — the strip is a
              shortcut to one priority, the panel still does multi-select. */}
          <div className="flex flex-wrap items-center gap-[6px] rounded-[10px] bg-[var(--co-selected)] p-1">
            <QuickTab
              label="All"
              count={countsAreArchiveWide ? serverTotal : rows.length}
              title={`Across ${countScope}`}
              active={selectedPriorities.length === 0}
              onClick={() => {
                setPage(1);
                setSelectedPriorities([]);
              }}
            />
            {PRIORITY_OPTIONS.slice().reverse().map((p) => (
              <QuickTab
                key={p}
                label={p}
                count={priorityCounts[p] || 0}
                title={`Across ${countScope}`}
                active={selectedPriorities.length === 1 && selectedPriorities[0] === p}
                tone={toneFor(p).fg}
                onClick={() => {
                  setPage(1);
                  setSelectedPriorities((prev) =>
                    prev.length === 1 && prev[0] === p ? [] : [p],
                  );
                }}
              />
            ))}
          </div>

          <div className="flex-1" />

          <div className="flex flex-none flex-wrap items-center gap-[10px]">
            <button
              ref={filterButtonRef}
              onClick={() => setShowFilters((v) => !v)}
              className={`${CONTROL} ${
                showFilters || activeFilterCount > 0
                  ? 'border-[var(--co-brass)] text-[var(--co-brass-ink)]'
                  : ''
              }`}
            >
              <SlidersHorizontal className="h-[14px] w-[14px]" />
              Filter
              {activeFilterCount > 0 && (
                <span className="co-mono text-[10px] font-semibold text-[var(--co-brass-ink)]">
                  {activeFilterCount}
                </span>
              )}
            </button>
            <button onClick={fetchRows} disabled={isLoading} className={CONTROL}>
              <RefreshCw className={`h-[14px] w-[14px] ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <ExportButton
              onFetchData={fetchBacktrackedForExport}
              columns={exportColumns}
              filename="backtracked-tickets"
              sheetName="Backtracked"
              variant="ghost"
              size="sm"
              className={`${CONTROL} !rounded-[10px]`}
            />
          </div>
        </div>
      </header>

      {/* Wide screens (1680px+): the search tab hangs off the card's bottom-right
          edge. Tablet and below: an ordinary full-width card under the header.
          (Wide-screen detail:) It overlaps the card's bottom border by 1px so the
          two read as one shape; the fillet draws the concave corner. */}
      <div
        className="relative z-[1] mt-[14px] flex items-center rounded-[14px] border border-[var(--co-line)] bg-[var(--co-raised)] px-4 py-3 min-[1680px]:absolute min-[1680px]:right-0 min-[1680px]:top-full min-[1680px]:-mt-px min-[1680px]:w-[332px] min-[1680px]:rounded-t-none min-[1680px]:border-t-0 min-[1680px]:pb-3 min-[1680px]:pt-2"
        style={{ boxShadow: 'var(--co-shadow-card)' }}
      >
        <span
          aria-hidden="true"
          className="pointer-events-none absolute -left-[15px] top-0 hidden h-[15px] w-[15px] min-[1680px]:block"
          style={{
            background:
              'radial-gradient(circle at 0 100%, transparent 14px, var(--co-line) 14px, var(--co-line) 15px, var(--co-raised) 15px)',
          }}
        />
          <div className="relative w-full">
            <Search className="pointer-events-none absolute left-[11px] top-1/2 z-10 h-[14px] w-[14px] -translate-y-1/2 text-[var(--co-ink-3)]" />
            <input
              type="text"
              placeholder="Search backtracked tickets…"
              value={searchQuery}
              onChange={(e) => {
                setPage(1);
                setSearchQuery(e.target.value);
              }}
              className="h-[34px] w-full rounded-[10px] border border-[var(--co-line-strong)] bg-[var(--co-raised)] pl-[32px] pr-8 text-[12.5px] text-[var(--co-ink)] placeholder:text-[var(--co-ink-3)] focus:border-[var(--co-brass)] focus:outline-none"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
                className="absolute right-[10px] top-1/2 z-10 -translate-y-1/2 text-[var(--co-ink-3)] hover:text-[var(--co-ink)]"
              >
                <X className="h-[14px] w-[14px]" />
              </button>
            )}
          </div>
      </div>

        {/* Filter panel — floating overlay anchored to the header, so it never
            pushes the table down or creates a squished inner scroll region. */}
        {showFilters && (
          <div
            ref={filterPanelRef}
            className={`${CARD_CLASS} co-scrollbar-none absolute right-5 top-full z-30 mt-2 max-h-[70vh] w-[30rem] max-w-[calc(100vw-3rem)] space-y-4 overflow-y-auto p-4`}
            style={{ borderRadius: 10, boxShadow: 'var(--co-shadow-pop)' }}
          >
            {/* Panel header: title + count + always-visible Reset */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="co-mono text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
                  Filters
                </span>
                {activeFilterCount > 0 && (
                  <span className="co-mono text-[10px] font-semibold text-[var(--co-brass-ink)]">
                    {activeFilterCount}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={clearFilters}
                disabled={activeFilterCount === 0}
                className="co-mono inline-flex items-center gap-1.5 rounded-md border border-[var(--co-line-strong)] px-[9px] py-[5px] text-[10px] font-semibold uppercase tracking-[.11em] text-[var(--co-ink-2)] hover:border-[var(--co-brass)] disabled:cursor-not-allowed disabled:opacity-40"
              >
                <RotateCcw className="h-[12px] w-[12px]" />
                Reset filters
              </button>
            </div>

            {/* Uniform responsive grid — one field per cell, date range spans full width */}
            <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
              {/* Priority */}
              <div>
                <FieldLabel>Priority</FieldLabel>
                <div className="flex flex-wrap gap-2">
                  {PRIORITY_OPTIONS.map((p) => {
                    const active = selectedPriorities.includes(p);
                    const tone = toneFor(p);
                    return (
                      <button
                        key={p}
                        type="button"
                        onClick={() => togglePriority(p)}
                        aria-pressed={active}
                        className="co-mono inline-flex items-center rounded-md border px-[9px] py-[5px] text-[10px] font-semibold uppercase tracking-[.1em]"
                        style={{
                          color: tone.fg,
                          background: active ? tone.bg : 'var(--co-raised)',
                          borderColor: active ? tone.fg : 'var(--co-line-strong)',
                        }}
                      >
                        {p}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Stage */}
              {availableStages.length > 0 && (
                <div>
                  <FieldLabel>Stage</FieldLabel>
                  <div className="flex flex-wrap gap-2">
                    {availableStages.map((s) => {
                      const active = s.ids.every((id) => selectedStages.includes(id));
                      return (
                        <button
                          key={s.key}
                          type="button"
                          onClick={() => toggleStageGroup(s.ids)}
                          aria-pressed={active}
                          className="inline-flex items-center gap-1.5 rounded-md border px-[9px] py-[5px] text-[11.5px]"
                          style={{
                            color: active ? 'var(--co-brass-ink)' : 'var(--co-ink-2)',
                            background: active ? 'var(--co-brass-wash)' : 'var(--co-raised)',
                            borderColor: active ? 'var(--co-brass)' : 'var(--co-line-strong)',
                            fontWeight: active ? 600 : 400,
                          }}
                        >
                          <span
                            className="h-1.5 w-1.5 flex-shrink-0 rounded-full"
                            style={{ backgroundColor: s.color || 'var(--co-ink-3)' }}
                          />
                          {s.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Pipeline */}
              {availablePipelines.length > 0 && (
                <div>
                  <FieldLabel>Pipeline</FieldLabel>
                  <ThemedSelect
                    aria-label="Pipeline"
                    value={selectedPipeline}
                    onChange={(e) => {
                      setPage(1);
                      setSelectedPipeline(e.target.value);
                    }}
                    className={SELECT_CLASS}
                  >
                    <option value="">All pipelines</option>
                    {availablePipelines.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </ThemedSelect>
                </div>
              )}

              {/* Assignee */}
              {(assigneeOptions.length > 0 || hasUnassigned) && (
                <div>
                  <FieldLabel>Assignee</FieldLabel>
                  <ThemedSelect
                    aria-label="Assignee"
                    value={selectedAssignee}
                    onChange={(e) => {
                      setPage(1);
                      setSelectedAssignee(e.target.value);
                    }}
                    className={SELECT_CLASS}
                  >
                    <option value="">Anyone</option>
                    {hasUnassigned && <option value={UNASSIGNED_ASSIGNEE}>Unassigned</option>}
                    {assigneeOptions.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </ThemedSelect>
                </div>
              )}

              {/* Backtracked date range — full width */}
              <div className="sm:col-span-2">
                <FieldLabel>Backtracked between</FieldLabel>
                <div className="flex items-center gap-3">
                  <input
                    type="date"
                    aria-label="Start date"
                    value={fromDate}
                    // Start date can't be after the end date, and never in the
                    // future. `toDate` is itself capped at today, so the tighter
                    // of the two applies.
                    max={toDate || todayStr}
                    onChange={(e) => {
                      setPage(1);
                      setFromDate(e.target.value);
                    }}
                    className={`min-w-0 flex-1 ${SELECT_CLASS}`}
                  />
                  <span className="text-[12px] text-[var(--co-ink-3)]">to</span>
                  <input
                    type="date"
                    aria-label="End date"
                    value={toDate}
                    // End date can't be before the start date, and never in the future.
                    min={fromDate || undefined}
                    max={todayStr}
                    onChange={(e) => {
                      setPage(1);
                      setToDate(e.target.value);
                    }}
                    className={`min-w-0 flex-1 ${SELECT_CLASS}`}
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-[var(--co-line-subtle)] pt-1">
              <p className="pt-3 text-[11.5px] text-[var(--co-ink-3)]">
                {total} matching {total === 1 ? 'ticket' : 'tickets'}
                {truncated && ` (filtering the ${FETCH_LIMIT} most recent archived tickets)`}
              </p>
              <button
                type="button"
                onClick={() => setShowFilters(false)}
                className="mt-3 flex h-[34px] flex-none items-center rounded-lg bg-[var(--co-brass-ink)] px-[14px] text-[12.5px] font-bold text-[var(--co-brass-on)] hover:opacity-90"
              >
                Apply
              </button>
            </div>
          </div>
        )}
      </div>


      {/* Below 1680px the rail moves under the table rather than
          squeezing it — the archive has seven columns and none may drop. */}
      <div className="flex min-h-0 flex-1 flex-col gap-[14px] overflow-y-auto min-[1680px]:flex-row min-[1680px]:overflow-hidden">
        <section
          className={`${CARD_CLASS} flex min-h-[320px] min-w-0 flex-none flex-col overflow-hidden min-[1680px]:flex-1`}
          style={CARD}
        >
          {error && (
            <div
              className="flex flex-none items-center gap-2 border-b px-5 py-3 text-[12.5px]"
              style={{
                borderColor: 'var(--co-burgundy-line)',
                background: 'var(--co-burgundy-wash)',
                color: 'var(--co-burgundy)',
              }}
            >
              <AlertCircle className="h-4 w-4 flex-none" />
              {error}
            </div>
          )}

          {/* A visible bar: below ~1260px the seven columns scroll sideways,
              and the bar is the only sign that they do. */}
          <div className="scrollbar-thin min-h-0 flex-1 overflow-auto">
            {isLoading && rows.length === 0 ? (
              <div className="flex h-64 items-center justify-center">
                <RefreshCw className="h-6 w-6 animate-spin text-[var(--co-ink-3)]" />
              </div>
            ) : pageRows.length === 0 ? (
              <div className="flex h-64 flex-col items-center justify-center text-[var(--co-ink-3)]">
                <ArchiveRestore className="mb-3 h-12 w-12 opacity-30" />
                <p className="text-[15px] font-semibold text-[var(--co-ink-2)]">
                  No backtracked tickets
                </p>
                <p className="mt-1 text-[12.5px]">
                  {searchQuery || activeFilterCount > 0
                    ? 'Try adjusting your search or filters'
                    : 'Closed tickets appear here at the end of the day'}
                </p>
              </div>
            ) : (
              <table className="w-full min-w-[1020px] table-fixed">
                <colgroup>
                  <col />
                  <col style={{ width: 104 }} />
                  <col style={{ width: 148 }} />
                  <col style={{ width: 168 }} />
                  <col style={{ width: 116 }} />
                  <col style={{ width: 128 }} />
                  <col style={{ width: 118 }} />
                </colgroup>
                {/* The rule under the header is a shadow so it stays with the
                    sticky row (a collapsed tr border scrolls away). */}
                <thead className="sticky top-0 z-10 bg-[var(--co-raised)] [&_th]:bg-[var(--co-raised)] [&_th]:shadow-[inset_0_-1px_0_var(--co-line)]">
                  <tr className="border-b border-[var(--co-line)]">
                    <SortableHeader field="subject" label="Ticket" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <SortableHeader field="priority" label="Priority" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <PlainHeader label="Assignee" />
                    <PlainHeader label="Pipeline" />
                    <SortableHeader field="createdAt" label="Created" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <SortableHeader field="backtrackedAt" label="Backtracked" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <PlainHeader label="Action" />
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((ticket) => (
                    <BacktrackRow
                      key={ticket.id}
                      ticket={ticket}
                      onNavigate={() => navigate(`/tickets/${ticket.id}`, { state: { fromBacktrack: true } })}
                      onReopen={() => setReopenTarget(ticket)}
                      isReopening={reopeningId === ticket.id}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination — full controls (item count + page-size selector + pages),
              same behaviour as the ticket list view. Shown whenever there are
              rows so the page-size selector is available even on a single page.

              This is the local, themed footer rather than @centraoneplus/ui's
              <Pagination>: that one is hard-coded gray/white with a blue-600
              active pill, which is off-palette inside the Ivory & Brass shell.
              Retheming it there would repaint seven other screens and rewrite
              its class assertions, so the fix stays on this card. */}
          {total > 0 && (
            <ArchiveFooter
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={total}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
              pageSizeOptions={[10, 20, 50, 100]}
            />
          )}
        </section>

        {/* On wide screens the search tab floats above this rail (same 332px
            width), so the table can start right under the header; the rail
            starts below the tab instead. */}
        <aside className="flex w-full flex-none flex-col gap-[14px] min-[1680px]:mt-[54px] min-[1680px]:w-[332px] co-scrollbar-none min-[1680px]:overflow-y-auto">
          {/* Absent when nothing is truncated — never a card that reads "0". */}
          {truncated && (
            <div
              className="flex-none border px-[18px] py-4"
              style={{
                ...CARD,
                borderColor: 'var(--co-terracotta-line)',
                background: 'var(--co-terracotta-wash)',
              }}
            >
              <span
                className="co-mono text-[10px] font-semibold uppercase tracking-[.16em]"
                style={{ color: 'var(--co-terracotta-ink)' }}
              >
                Showing {rows.length} of {serverTotal}
              </span>
              <p className="mt-[9px] text-[12.5px] leading-[1.6] text-[var(--co-ink-2)]">
                Search, filters and sorting run over the {FETCH_LIMIT} most recently archived
                tickets — not the whole archive. Narrow the date range to reach older ones.
              </p>
            </div>
          )}

          <div className={`${CARD_CLASS} flex-none px-[18px] py-4`} style={CARD}>
            <span className="co-mono text-[10px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
              Archived by day
            </span>
            <div className="mt-[14px] flex h-20 items-end gap-[7px]">
              {byDay.map((d) => (
                <div key={d.key} className="flex h-full flex-1 flex-col justify-end gap-[6px]">
                  <div
                    className="rounded-t-[3px]"
                    style={{
                      height: `${d.count ? (d.count / dayPeak) * 100 : 2}%`,
                      background: d.count ? 'var(--co-line-strong)' : 'var(--co-line-subtle)',
                    }}
                  />
                  <div className="co-mono text-center text-[8.5px] text-[var(--co-ink-3)]">
                    {d.label}
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-[11px] border-t border-[var(--co-line-subtle)] pt-[11px] text-[12px] leading-[1.5] text-[var(--co-ink-3)]">
              The last eight days of archiving, over the rows currently filtered.
            </p>
          </div>

          {ownerTally.length > 0 && (
            <div className={`${CARD_CLASS} flex min-h-0 flex-none flex-col px-[18px] py-4`} style={CARD}>
              {/* One line: as separate flex-column children the scope dropped to
                  a second row and left its "·" orphaned. */}
              <div className="flex items-baseline whitespace-nowrap">
              <span className="co-mono text-[10px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
                Who owned them
              </span>
              {/* Same scope as the table beneath it — named, not implied. */}
              <span className="co-mono ml-2 text-[9.5px] uppercase tracking-[.1em] text-[var(--co-ink-3)]">
                · {total} filtered
              </span>
              </div>
              <div className="mt-[13px] flex flex-col gap-[11px]">
                {ownerTally.slice(0, 8).map((o) => (
                  <div key={o.key} className="flex items-center gap-[10px]">
                    <span
                      className="flex h-6 w-6 flex-none items-center justify-center rounded-[7px] text-[10px] font-semibold"
                      style={
                        o.none
                          ? {
                              border: '1px solid var(--co-line-strong)',
                              color: 'var(--co-ink-3)',
                            }
                          : {
                              background: 'var(--co-ink-3)',
                              color: 'var(--co-ink-invert)',
                            }
                      }
                    >
                      {o.none ? '—' : o.name.charAt(0).toUpperCase()}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[12.5px] text-[var(--co-ink-2)]">
                      {o.name}
                    </span>
                    <span className="co-mono text-[12px] font-semibold text-[var(--co-ink)]">
                      {o.count}
                    </span>
                  </div>
                ))}
              </div>
              {ownerTally[0]?.none && (
                <p className="mt-3 border-t border-[var(--co-line-subtle)] pt-3 text-[12px] leading-[1.5] text-[var(--co-ink-3)]">
                  More of these closed unowned than with an owner. Worth knowing before you read
                  anything into the archive.
                </p>
              )}
            </div>
          )}
        </aside>
      </div>

      {/* Reopen Modal */}
      <ReopenTicketModal
        isOpen={!!reopenTarget}
        onClose={() => {
          if (reopeningId !== reopenTarget?.id) setReopenTarget(null);
        }}
        onSubmit={handleReopenSubmit}
        ticketNumber={reopenTarget?.ticketNumber}
        ticketSubject={reopenTarget?.subject}
        pipelineId={reopenTarget?.pipelineId ?? null}
        isSubmitting={!!reopenTarget && reopeningId === reopenTarget.id}
      />
    </div>
  );
}

const SELECT_CLASS =
  'w-full rounded-lg border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 py-2 ' +
  'text-[12.5px] text-[var(--co-ink)] focus:border-[var(--co-brass)] focus:outline-none';

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="co-mono mb-2 block text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
      {children}
    </label>
  );
}

/**
 * The card's footer: item count on the left, the shared page controls on the
 * right, in the product palette.
 *
 * The page buttons, the page-size select and the 7-visible-with-ellipses
 * window used to live here in full - this screen needed a themed pager before
 * one existed. They are now `Pager` in the kit, unchanged in behaviour, so the
 * ticket register and this card cannot drift apart.
 */
function ArchiveFooter({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions,
}: {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  pageSizeOptions: number[];
}) {
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  return (
    <div
      className="flex flex-none flex-wrap items-center gap-x-5 gap-y-2 border-t border-[var(--co-line)] bg-[var(--co-selected)] px-5 py-[6px]"
      style={{ borderRadius: '0 0 9px 9px' }}
    >
      <span className="co-mono whitespace-nowrap text-[10.5px] uppercase tracking-[.1em] text-[var(--co-ink-3)]">
        {startItem}–{endItem} of {totalItems}
      </span>

      <div className="flex-1" />

      <Pager
        id="backtrack"
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        pageSizeOptions={pageSizeOptions}
      />
    </div>
  );
}

function QuickTab({
  label,
  count,
  active,
  tone,
  title,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  tone?: string;
  /** What set the count is about — the tab says so rather than implying it. */
  title?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={title}
      className="co-mono flex items-center gap-[6px] rounded-lg px-3 py-[7px] text-[10.5px] uppercase tracking-[.12em]"
      style={
        active
          ? {
              background: 'var(--co-raised)',
              color: 'var(--co-ink)',
              fontWeight: 600,
              boxShadow: 'var(--co-shadow)',
            }
          : { color: 'var(--co-ink-3)', fontWeight: 500 }
      }
    >
      {label}
      <span
        className="font-semibold"
        style={{ color: active ? tone || 'var(--co-brass-ink)' : 'var(--co-ink-3)' }}
      >
        {count}
      </span>
    </button>
  );
}
